import { IMAGE_VARIANTS } from './constants.js';
/** Wie viel Luft standardmäßig um eine Markierung bleibt. */
export const CROP_PADDING = 0.3;
/** Markierung mit etwas Luft drumherum – Gesichter wirken sonst angeschnitten. */
export function padBox(box, factor = CROP_PADDING) {
    const growX = box.width * factor;
    const growY = box.height * factor;
    const x = Math.max(0, box.x - growX / 2);
    const y = Math.max(0, box.y - growY / 2);
    return {
        x,
        y,
        width: Math.min(1 - x, box.width + growX),
        height: Math.min(1 - y, box.height + growY),
    };
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/**
 * Der Bildausschnitt, der in den Rahmen gehört – normalisiert wie die
 * Markierung selbst.
 *
 * Erst kommt Luft um die Markierung (`padBox`), dann wird der Ausschnitt auf
 * das Seitenverhältnis des Rahmens gebracht. Sind die Bildmaße unbekannt,
 * bleibt es bei der gepolsterten Markierung – wer damit zeichnet, muss dann
 * selbst dafür sorgen, dass beschnitten statt gestaucht wird (`object-fit:
 * cover` bzw. `preserveAspectRatio="slice"`).
 *
 * Das Seitenverhältnis wird in **Bildpixeln** korrigiert, nicht in
 * normalisierten Koordinaten – sonst wird bei hochkant aufgenommenen Bildern
 * jedes Gesicht in die Länge gezogen.
 */
export function cropBox(region, { padding = CROP_PADDING, aspect = 1, imageWidth, imageHeight, } = {}) {
    const padded = padBox(region, padding);
    if (!imageWidth || !imageHeight)
        return padded;
    // Der kleinste Ausschnitt im Seitenverhältnis des Rahmens, der die
    // Markierung noch ganz enthält – gemessen in Bildpixeln.
    let boxW = Math.max(padded.width * imageWidth, padded.height * imageHeight * aspect);
    let boxH = boxW / aspect;
    // Ragt er über eine Kante hinaus, schrumpfen beide Seiten im gleichen
    // Verhältnis. Ein einzelnes Kappen würde das Format zerstören.
    const shrink = Math.min(1, imageWidth / boxW, imageHeight / boxH);
    boxW *= shrink;
    boxH *= shrink;
    const width = boxW / imageWidth;
    const height = boxH / imageHeight;
    return {
        width,
        height,
        x: clamp(padded.x + padded.width / 2 - width / 2, 0, Math.max(0, 1 - width)),
        y: clamp(padded.y + padded.height / 2 - height / 2, 0, Math.max(0, 1 - height)),
    };
}
/** Die Größen, in denen Porträts ausgeliefert werden. Geschlossene Liste. */
export const CROP_SIZES = [64, 128, 256, 512];
/** Die Rahmenformate, die die Oberfläche kennt: rund/quadratisch und 4:5. */
export const CROP_ASPECTS = [1, 0.8];
/** Die kleinste angebotene Größe, die für `renderedPx` noch reicht. */
export function cropSizeFor(renderedPx) {
    // Faktor 1.3 als Reserve für Bildschirme mit doppelter Pixeldichte – dieselbe
    // Überlegung wie bei der Variantenwahl, siehe `variantForCrop`.
    const needed = renderedPx * 1.3;
    return CROP_SIZES.find((size) => size >= needed) ?? 512;
}
/**
 * Welche gespeicherte Variante als Vorlage für einen Ausschnitt genügt.
 *
 * Ein Gesicht, das ein Fünftel der Bildbreite einnimmt, braucht für 128px
 * Darstellung ein Bild von rund 640px – nicht das 2048px-Original. Die Wahl
 * gehört hierher und nicht in den Browser, weil der Server denselben Schluss
 * ziehen muss, wenn er den Ausschnitt wirklich rendert.
 *
 * `null` heißt: keine Variante reicht, dann nimmt der Aufrufer die größte
 * vorhandene.
 */
export function variantForCrop(targetPx, boxWidth, available) {
    const share = Math.max(boxWidth, 0.02);
    const needed = targetPx / share;
    for (const variant of ['thumb', 'medium', 'large']) {
        if (available.includes(variant) && IMAGE_VARIANTS[variant].width >= needed)
            return variant;
    }
    return null;
}
//# sourceMappingURL=crop.js.map